// ============================================================
// Öğrenci güvenlik ve sağlık bilgisi (§3)
//
// Tohum veride her öğrencinin tek bir `parent` alanı var. Gerçek
// hayatta bu yetmiyor: ikinci veli, çocuğu almaya yetkili kişiler,
// velayet kısıtı ve acil durum kişisi ayrı ayrı bilinmeli.
//
// Buradaki yardımcılar iki kaynağı birleştirir:
//   - student.parent / parentPhone  → birincil veli (her zaman var)
//   - SAFETY[branchId][studentId]   → ek veliler, yetkili kişiler,
//                                     kısıtlar, sağlık ayrıntısı
//
// Böylece hiçbir öğrenci velisiz kalmaz, ayrıntı gerektiren kayıtlar
// da eksiksiz görünür.
// ============================================================

import { state } from "../state.js";

// Teslim/acil durum bilgisi. Gerçek modda öğrenci kaydının kendi
// alanından gelir; demo modda data/safety.js buraya enjekte edilir.
// Statik import edilemez — o dosya 55 kişisel kayıt taşıyor ve
// üretim paketine girerdi.
let guvenlikKaynagi = {};
export const guvenlikKaynaginiAyarla = (kaynak) => { guvenlikKaynagi = kaynak ?? {}; };

const guvenlik = (student, branchId = state.branchId) =>
  student.safety ?? guvenlikKaynagi[branchId]?.[student.id] ?? {};

// ---------- Veliler ----------

/** Birincil veli — tohum verideki parent alanından türetilir. */
export const birincilVeli = student => ({
  name: student.parent,
  phone: student.parentPhone,
  relation: "Veli",
  isPrimary: true,
  canPickup: true,
  custody: true,
});

/** Tüm veliler: birincil + kayıtlı ikinci veli(ler). */
export function veliler(student, branchId) {
  return [birincilVeli(student), ...(guvenlik(student, branchId).guardians ?? [])];
}

// ---------- Teslim yetkisi ----------

/** Çocuğu teslim alabilecek herkes — veliler ve yetkilendirilmiş kişiler. */
export function alabilecekler(student, branchId) {
  const g = guvenlik(student, branchId);
  return [
    ...veliler(student, branchId).filter(v => v.canPickup),
    ...(g.authorizedPickup ?? []),
  ];
}

/** Teslim edilemeyecek kişiler — velayet kısıtı olanlar. */
export function alamayanlar(student, branchId) {
  return veliler(student, branchId).filter(v => v.canPickup === false);
}

/** Serbest metin teslim kısıtı; doluysa arayüzde kırmızı uyarı çıkar. */
export const teslimKisiti = (student, branchId) =>
  guvenlik(student, branchId).pickupRestrictions ?? "";

// ---------- Acil durum & sağlık ----------

export function acilDurum(student, branchId) {
  return guvenlik(student, branchId).emergency ?? {
    name: student.parent,
    phone: student.parentPhone,
    relation: "Veli",
  };
}

export const saglik = (student, branchId) => guvenlik(student, branchId).medical ?? null;

// ---------- Alerjiler ----------

export const SIDDET = {
  hafif:      { label: "Hafif",       renk: "sun",   sira: 1 },
  orta:       { label: "Orta",        renk: "sun",   sira: 2 },
  agir:       { label: "Ağır",        renk: "coral", sira: 3 },
  anafilaksi: { label: "Anafilaksi",  renk: "coral", sira: 4 },
};

/**
 * Alerjileri şiddet ve müdahale bilgisiyle birlikte döner.
 * Şiddeti kayıtlı olmayan alerji "orta" sayılır — bilinmeyen bir
 * alerjiyi hafif varsaymak güvenli tarafta kalmaz.
 */
export function alerjiler(student, branchId) {
  const bilgi = guvenlik(student, branchId).allergyInfo ?? {};
  return (student.allergies ?? [])
    .map(ad => ({
      name: ad,
      severity: bilgi[ad]?.severity ?? "orta",
      response: bilgi[ad]?.response ?? "Müdahale talimatı kayıtlı değil — veli aranmalı.",
    }))
    .sort((a, b) => SIDDET[b.severity].sira - SIDDET[a.severity].sira);
}

/** En yüksek şiddet — kartlarda rozet rengi için. */
export function enAgirAlerji(student, branchId) {
  const list = alerjiler(student, branchId);
  return list.length ? list[0] : null;
}

/** Anafilaksi riski var mı — kalıcı kırmızı uyarı gerektirir. */
export const hayatiRisk = (student, branchId) =>
  alerjiler(student, branchId).some(a => a.severity === "anafilaksi");

/** Sağlık takibi gerektiren öğrenciler (alerji veya tanı). */
export function saglikTakipli(students, branchId) {
  return students.filter(s =>
    (s.allergies?.length ?? 0) > 0 ||
    (saglik(s, branchId)?.conditions?.length ?? 0) > 0);
}
